import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Navigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Switch} from '../../buttons/Swtich';
import {Check, EnvelopeSimple, Lock, Plus} from '../../icons';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {Spinner} from '../../Spinner';
import {
    AUDIENCE_ADMIN,
    audienceOf,
    availableChannels,
    CHANNEL_EMAIL,
    CHANNEL_WEBHOOK,
    draftFromRule,
    exceptNames,
    isActiveRule,
    isConverted,
    isRuleOn,
    namesText,
    paraLine,
    partition,
    RECIPIENTS_ADMINS,
    RECIPIENTS_ALL,
    RECIPIENTS_ASSIGNED,
    RECIPIENTS_USERS,
    ruleAnchorId,
    ruleChannels,
    teamActionOf,
    TEAM_RECIPIENT_TYPES,
} from '../rulesModel';
import {alertTitle, controlDescription} from '../ruleText';
import {ALERTS_RULES, PARAM_PAUSE, PARAM_RULE} from '../alertsCenterPaths';
import {ChipMenu} from '../builder/ChipMenu';
import {RecipientsPicker} from '../builder/RecipientsPicker';
import {WebhookSetupDrawer} from './WebhookSetupDrawer';
import {
    connectedWebhook,
    eventsForSave,
    rowWebhookEvents,
    WEBHOOK_STATE_NONE,
    WEBHOOK_STATE_UNAVAILABLE,
    webhookStatus,
} from './webhookModel';
import {WEBHOOK_OK} from './ChannelsCard';
import {useRuleWrites} from './useRuleWrites';

const HIGHLIGHT_MS = 2500;

// Las opciones de «Le llega a», en el orden del mockup. «Toda la agencia» se ofrece sólo si el aviso ya está así: el
// mockup no la tiene y no se esconde lo que hay.
const BASE_CHOICES = [RECIPIENTS_ASSIGNED, RECIPIENTS_ADMINS, RECIPIENTS_USERS];
const CHOICE_KEYS = {
    [RECIPIENTS_ASSIGNED]: 'alerts_center.accounts.option_assigned',
    [RECIPIENTS_ALL]: 'alerts_center.accounts.option_all',
    [RECIPIENTS_ADMINS]: 'alerts_center.accounts.option_admins',
    [RECIPIENTS_USERS]: 'alerts_center.accounts.option_users',
};

/**
 * Los nombres de las bajas elegidas: de las que resolvió el backend o, si no están ahí, del equipo. Quien mira es «tú»,
 * como en la fila de «Mis reglas».
 */
function excludedNames(ids, rule, team, {t, viewerId}) {
    const fromRule = (rule.delivery && rule.delivery.recipients && rule.delivery.recipients.excluded) || [];
    const byId = new Map([...fromRule, ...(team || [])].map((user) => [String(user.id), user.name || user.email])
        .filter(([, name]) => name));
    return exceptNames(ids, byId, {t, viewerId}).names;
}

/**
 * El selector de «Le llega a» de un aviso (UD-18): todo el equipo de cada cliente (con «salvo …»), los
 * administradores (con «salvo …») o algunas personas. Guarda con «Listo», con el mismo PUT del «Para» del constructor.
 */
function RecipientsForm({rule, options, team, close, onSave, teamState = 'ok', onRetryTeam = null, viewerId = null}) {
    const {t} = useTranslation();
    const initial = useMemo(() => draftFromRule(rule).recipients, [rule]);
    const [recipients, setRecipients] = useState(initial);
    const [picking, setPicking] = useState(null);
    const choices = initial.type === RECIPIENTS_ALL ? [RECIPIENTS_ASSIGNED, RECIPIENTS_ALL, RECIPIENTS_ADMINS, RECIPIENTS_USERS]
        : BASE_CHOICES;
    const isTeam = TEAM_RECIPIENT_TYPES.includes(recipients.type);
    const invalid = recipients.type === RECIPIENTS_USERS && recipients.userIds.length === 0;

    const choose = (type) => {
        setPicking(type === RECIPIENTS_USERS ? 'users' : null);
        setRecipients((current) => ({
            ...current,
            type,
            userIds: type === RECIPIENTS_USERS ? current.userIds : [],
            // Las bajas son de las reglas de equipo: al pasar a personas, se van (el backend las borra igual).
            excludedUserIds: TEAM_RECIPIENT_TYPES.includes(type) ? current.excludedUserIds : [],
        }));
    };

    const save = () => {
        if (invalid) return;
        const same = recipients.type === initial.type
            && String(recipients.userIds) === String(initial.userIds)
            && String(recipients.excludedUserIds) === String(initial.excludedUserIds);
        if (!same) onSave(rule, {...recipients, excludedKnown: initial.excludedKnown || recipients.excludedUserIds.length > 0});
        close({focusChip: true});
    };

    const except = excludedNames(recipients.excludedUserIds || [], rule, team, {t, viewerId});
    // Sin las personas del equipo no hay a quién elegir: se dice (y se reintenta) en vez de abrir un selector vacío
    // (review C-ud18-fe-06), como el filtro «Le llegan a» de la lista.
    const peopleMissing = (!options || !Array.isArray(options.users)) && teamState !== 'ok';
    const renderPeople = (userIds, onChange) => {
        if (peopleMissing && teamState === 'error') {
            return (
                <p className="alc-hint" role="alert" data-testid="alc-acc-team-error">
                    {t('alerts_center.v2.view_as_team_error')}
                    {onRetryTeam && (
                        <>
                            {' · '}
                            <button type="button" className="alc-link -button" onClick={onRetryTeam}>{t('alerts_center.retry')}</button>
                        </>
                    )}
                </p>
            );
        }
        if (peopleMissing) return <div className="box-loader" data-testid="alc-acc-team-loading"><Spinner color="positive small"/></div>;
        return <RecipientsPicker userIds={userIds} options={options} onChange={onChange}/>;
    };
    return (
        <div className="alc-acc-pop" data-testid="alc-acc-recipients">
            <p className="alc-acc-pop__title">{t('alerts_center.accounts.recipients_title')}</p>
            {choices.map((type) => (
                <div key={type} className="alc-acc-pop__choice">
                    <label className="alc-acc-opt">
                        <input type="radio" name={`alc-acc-para-${rule.id}`} value={type} checked={recipients.type === type}
                               onChange={() => choose(type)}/>
                        <span>{t(CHOICE_KEYS[type])}</span>
                    </label>
                    {recipients.type === type && isTeam && (
                        <p className="alc-acc-pop__salvo" data-testid="alc-acc-except">
                            {except.length
                                ? t('alerts_center.builder.except_names', {names: namesText(except, t), interpolation: {escapeValue: false}})
                                : t('alerts_center.builder.except_none')}
                            {' '}
                            <button type="button" className="alc-link -button" aria-expanded={picking === 'except'}
                                    onClick={() => setPicking(picking === 'except' ? null : 'except')}>
                                {t('alerts_center.accounts.change')}
                            </button>
                        </p>
                    )}
                </div>
            ))}
            {picking === 'except' && isTeam
                && renderPeople(recipients.excludedUserIds || [],
                    (excludedUserIds) => setRecipients((current) => ({...current, excludedUserIds})))}
            {recipients.type === RECIPIENTS_USERS
                && renderPeople(recipients.userIds, (userIds) => setRecipients((current) => ({...current, userIds})))}
            {invalid && !peopleMissing && (
                <p className="alc-hint" data-testid="alc-acc-users-empty">{t('alerts_center.accounts.users_empty')}</p>
            )}
            <div className="alc-acc-pop__foot">
                <button type="button" className="alc-btn -primary -sm" disabled={invalid} onClick={save} data-testid="alc-acc-done">
                    {t('alerts_center.accounts.done')}
                </button>
            </div>
        </div>
    );
}

/**
 * «Avisos de las cuentas» (`/alerts/rules/cuentas`, UD-18, 24/10): los avisos de control de Dashbo, en su propia
 * página. Un renglón por aviso: qué mira, «Le llega a» (el selector, en el lugar), «Por» (el correo fijo y el chip
 * «Webhook» si el aviso lo admite; si no, «sólo correo») y «Activo» / «Pausado» con el switch. Sin «Editar»: un aviso
 * de cuenta no tiene número. Pausado, la fila queda en gris y sus chips no se tocan; igual se ve en «Ahora» (los
 * managers registran en la Bitácora aunque la fila esté pausada).
 *
 * Un no admin la ve en sólo lectura (D13): sin selector ni switch. `?regla=` resalta un aviso y `?pausar=1` (el link
 * de un correo) pregunta en su fila. La marca de deploy es `data-testid="alc-accounts"`.
 */
export function AccountNoticesView({payload, loading, loadError, onRetry, onReload, onPayloadChange, canEdit = true,
                                       me = null, team = null, options = null, webhook = null, onWebhookSaved,
                                       onWebhookRetry, userId = null, teamState = 'ok', onRetryTeam = null}) {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const ruleParam = searchParams.get(PARAM_RULE);
    const pauseParam = searchParams.get(PARAM_PAUSE) === '1';
    const [highlightId, setHighlightId] = useState(null);
    const [pauseAskId, setPauseAskId] = useState(null);
    const [pendingWebhookRule, setPendingWebhookRule] = useState(null);
    const highlightTimer = useRef(null);
    const pauseRef = useRef(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(highlightTimer.current);
        };
    }, []);

    const {alerts, controls} = useMemo(() => partition(payload), [payload]);
    const viewer = me || (payload ? payload.viewer : null) || null;
    const admin = audienceOf(viewer) === AUDIENCE_ADMIN;
    const viewerId = viewer && viewer.id !== undefined ? viewer.id : userId;
    const reload = onReload || onRetry;
    const writes = useRuleWrites({onPayloadChange, reload, limits: payload ? payload.limits : null});
    const webhookState = webhookStatus(payload && payload.webhook, webhook);

    const highlight = useCallback((id) => {
        clearTimeout(highlightTimer.current);
        setHighlightId(String(id));
        highlightTimer.current = setTimeout(() => {
            if (mounted.current) setHighlightId(null);
        }, HIGHLIGHT_MS);
    }, []);

    useEffect(() => {
        if (highlightId === null) return;
        const row = document.getElementById(ruleAnchorId(highlightId));
        if (row && row.scrollIntoView) row.scrollIntoView({block: 'center', behavior: 'smooth'});
    }, [highlightId]);

    // `?regla=<id>` (y `?pausar=1`): una vez por valor.
    const handledParam = useRef(null);
    useEffect(() => {
        if (!ruleParam || !payload || handledParam.current === ruleParam) return;
        handledParam.current = ruleParam;
        const target = controls.find((rule) => String(rule.id) === String(ruleParam));
        if (!target) return;
        highlight(target.id);
        if (pauseParam) setPauseAskId(String(target.id));
    }, [ruleParam, pauseParam, payload, controls, highlight]);

    const dismissPause = () => {
        setPauseAskId(null);
        const next = new URLSearchParams(searchParams);
        next.delete(PARAM_PAUSE);
        setSearchParams(next, {replace: true});
    };

    useEffect(() => {
        if (pauseAskId === null || !pauseRef.current) return;
        const button = pauseRef.current.querySelector('button');
        if (button) button.focus();
    }, [pauseAskId]);

    // ------------------------------------------------------------------ webhook ---------------------
    const governedEvents = rowWebhookEvents([...alerts, ...controls]);
    const rowsWithWebhook = rowWebhookEvents([...alerts, ...controls]
        .filter((rule) => isActiveRule(rule) && ruleChannels(rule).includes(CHANNEL_WEBHOOK)));
    const knownEvents = webhook && webhook.status === WEBHOOK_OK
        ? eventsForSave(webhook.selected || [], {governed: governedEvents, on: rowsWithWebhook}) : null;

    const addWebhook = (rule) => writes.setChannels(rule, [...ruleChannels(rule).filter((channel) => channel !== CHANNEL_WEBHOOK),
        CHANNEL_WEBHOOK], {onNeedsWebhook: setPendingWebhookRule});

    const toggleWebhook = (rule) => {
        if (ruleChannels(rule).includes(CHANNEL_WEBHOOK)) {
            writes.setChannels(rule, ruleChannels(rule).filter((channel) => channel !== CHANNEL_WEBHOOK));
            return;
        }
        // Sin la dirección conectada, primero el panel de 3 pasos; al guardarla, se prende en esta fila.
        if (webhookState.state === WEBHOOK_STATE_NONE) {
            setPendingWebhookRule(rule);
            return;
        }
        addWebhook(rule);
    };

    const webhookConnected = ({url, destination, webhook: saved}) => {
        onPayloadChange((current) => ({...current, webhook: saved || connectedWebhook(current && current.webhook, destination)}));
        if (!saved && reload) reload();
        if (webhook && webhook.status === WEBHOOK_OK) onWebhookSaved({...webhook, url, selected: knownEvents || webhook.selected});
        else if (onWebhookRetry) onWebhookRetry();
        const pending = pendingWebhookRule;
        setPendingWebhookRule(null);
        if (pending) {
            const fresh = controls.find((rule) => String(rule.id) === String(pending.id)) || pending;
            addWebhook(fresh);
        }
    };

    // ------------------------------------------------------------------ dibujo ----------------------
    if (loadError !== null && loadError !== undefined) {
        return (
            <LoadErrorState title={loadError} text={t('alerts_center.load_error_explanation')}
                            retryLabel={t('alerts_center.retry')} onRetry={onRetry}/>
        );
    }
    if (!payload) return loading !== false ? <div className="box-loader"><Spinner color="positive"/></div> : null;
    // Una agencia sin convertir no tiene avisos de control como filas: la lista de siempre.
    if (!isConverted(payload)) return <Navigate to={ALERTS_RULES} replace/>;

    const renderPauseConfirm = (rule) => {
        const action = teamActionOf(rule);
        const canPause = canEdit && (typeof rule.can_pause === 'boolean' ? rule.can_pause : rule.can_edit !== false);
        return (
            <div className="alc-confirm alc-v2-pause" role="alertdialog" aria-labelledby="alc-acc-pause-title"
                 data-testid="alc-pause-confirm" ref={pauseRef}>
                <p id="alc-acc-pause-title" className="alc-confirm__text">{t('alerts_center.row.pause_question_r5')}</p>
                <div className="alc-confirm__acts">
                    {action && action.next && (
                        <button type="button" className="alc-btn -primary" onClick={() => { writes.optOut(rule, true); dismissPause(); }}>
                            {t('alerts_center.row.stop_receiving')}
                        </button>
                    )}
                    {canPause && isRuleOn(rule) && (
                        <button type="button" className="alc-btn -ghost"
                                onClick={() => { writes.toggleActive(rule, false); dismissPause(); }}>
                            {t('alerts_center.row.pause_for_everyone_r5')}
                        </button>
                    )}
                    <button type="button" className="alc-btn -ghost" onClick={dismissPause}>{t('alerts_center.row.pause_keep')}</button>
                </div>
            </div>
        );
    };

    const renderRow = (rule) => {
        const id = String(rule.id);
        const anchorId = ruleAnchorId(rule.id);
        const titleId = `${anchorId}-title`;
        const stateId = `${anchorId}-state`;
        const on = isRuleOn(rule);
        const busy = writes.isBusy(rule.id);
        const editable = canEdit && admin && rule.can_edit !== false;
        const canPause = canEdit && (typeof rule.can_pause === 'boolean' ? rule.can_pause : editable);
        const available = availableChannels(rule);
        const hasEmail = available.includes(CHANNEL_EMAIL);
        const webhookOn = ruleChannels(rule).includes(CHANNEL_WEBHOOK);
        // Con «Para» adelante, como en la fila de «Mis reglas»: la celda se lee sola («Para ti y Ana Pérez»). Con
        // mayúscula y sin «Para» decía «Ti y Ana Pérez».
        const who = hasEmail
            ? t('alerts_center.accounts.para_cell', {who: paraLine(rule, {t, viewerId, team}), interpolation: {escapeValue: false}})
            : null;
        // Pausado: los chips se ven y no se tocan (UD-18).
        const locked = !on || busy;
        const webhookUnavailable = webhookState.state === WEBHOOK_STATE_UNAVAILABLE;

        const renderWebhook = () => {
            if (!available.includes(CHANNEL_WEBHOOK)) return hasEmail ? <span className="alc-acc-only">{t('alerts_center.accounts.only_email')}</span> : null;
            const content = (
                <>
                    {webhookOn ? <Check size={14} weight="bold" aria-hidden="true"/> : <Plus size={14} weight="bold" aria-hidden="true"/>}
                    {t('alerts_center.row.channel_webhook')}
                </>
            );
            if (!editable) return <span className={`alc-ch -static ${webhookOn ? '-on' : '-off'}`}>{content}</span>;
            return (
                <button type="button" className={`alc-ch ${webhookOn ? '-on' : '-off'}`} aria-pressed={webhookOn}
                        disabled={locked || (webhookUnavailable && !webhookOn)} data-testid="alc-acc-webhook"
                        onClick={() => toggleWebhook(rule)}>
                    {content}
                </button>
            );
        };

        return (
            <li key={id} id={anchorId} data-rule-id={rule.id} aria-labelledby={titleId} data-on={String(on)}
                className={`alc-acc-row${on ? '' : ' -paused'}${highlightId === id ? ' -highlight' : ''}`}>
                <div className="alc-acc-row__what">
                    <h3 id={titleId} className="alc-acc-row__title">{alertTitle(rule, t)}</h3>
                    <span className="alc-acc-row__desc">{controlDescription(rule, t)}</span>
                </div>
                <div className="alc-acc-row__para">
                    <span className="alc-acc-row__label">{t('alerts_center.accounts.col_recipients')}</span>
                    {who && (editable ? (
                        <ChipMenu slot="recipients" label={who} buttonClassName="alc-bc -sm" disabled={locked}
                                  menuLabel={t('alerts_center.accounts.recipients_title')}>
                            {({close}) => (
                                <RecipientsForm rule={rule} options={options} team={team} close={close}
                                                onSave={writes.setRecipients} teamState={teamState}
                                                onRetryTeam={onRetryTeam} viewerId={viewerId}/>
                            )}
                        </ChipMenu>
                    ) : <span className="alc-acc-text" data-testid="alc-acc-para">{who}</span>)}
                </div>
                <div className="alc-acc-row__por">
                    <span className="alc-acc-row__label">{t('alerts_center.accounts.col_channels')}</span>
                    {hasEmail && (
                        <span className="alc-ch -on -locked" aria-label={t('alerts_center.builder.email_always_label')}>
                            <EnvelopeSimple size={15} aria-hidden="true"/>
                            {t('alerts_center.row.channel_email')}
                            <Lock size={13} className="alc-ch__lock" aria-hidden="true"/>
                        </span>
                    )}
                    {renderWebhook()}
                </div>
                <div className="alc-acc-row__acts">
                    <span id={stateId} className={`alc-mr-row__state${on ? ' -on' : ''}`} data-testid="alc-row-state">
                        {t(on ? 'alerts_center.accounts.state_active' : 'alerts_center.accounts.state_paused')}
                    </span>
                    {canPause && editable && (
                        <span className="alc-mr-row__switch" data-testid="alc-row-toggle">
                            <Switch name={`${anchorId}-active`} className="alc-switch" checked={on} disabled={busy}
                                    aria-labelledby={`${titleId} ${stateId}`}
                                    onChange={(event) => writes.toggleActive(rule, event.target.checked)}/>
                        </span>
                    )}
                </div>
                {pauseAskId === id && renderPauseConfirm(rule)}
            </li>
        );
    };

    return (
        <div className="alc-acc" data-testid="alc-accounts">
            <h2 className="alc-ed-title">{t('alerts_center.accounts.title')}</h2>
            <p className="alc-ed-sub">{t('alerts_center.accounts.lede')}</p>
            {!canEdit && <p className="alc-notice -warn" role="note" data-testid="alc-read-only">{t('alerts_center.no_edit_permission')}</p>}
            {controls.length === 0 ? (
                <p className="alc-empty -inline">{t('alerts_center.v2.controls_empty')}</p>
            ) : (
                <div className="alc-acc-table">
                    <div className="alc-acc-head" aria-hidden="true">
                        <span>{t('alerts_center.accounts.col_notice')}</span>
                        <span>{t('alerts_center.accounts.col_recipients')}</span>
                        <span>{t('alerts_center.accounts.col_channels')}</span>
                        <span/>
                    </div>
                    <ul className="alc-acc-list">{controls.map(renderRow)}</ul>
                </div>
            )}
            {pendingWebhookRule && (
                <WebhookSetupDrawer pendingRuleTitle={alertTitle(pendingWebhookRule, t)}
                                    initialUrl={(webhook && webhook.url) || ''} initialDestination={webhookState.destination}
                                    events={knownEvents} onSaved={webhookConnected}
                                    onTested={() => { if (reload) reload(); }} onClose={() => setPendingWebhookRule(null)}/>
            )}
        </div>
    );
}

export default AccountNoticesView;
